/**
 * lucide-react アイコンの動的取得 + 選択用候補リスト。
 *
 * - LUCIDE_ICON_NAMES: ピッカー UI 用のおすすめアイコン名リスト（kebab-case）
 * - getLucideIcon(name): 名前から React コンポーネントを取得
 */
import { icons, type LucideIcon } from 'lucide-react';

export const LUCIDE_ICON_NAMES = [
  // 一般
  'check', 'check-circle', 'star', 'heart', 'sparkles', 'sparkle', 'eye',
  'thumbs-up', 'award', 'trophy', 'gift', 'crown',
  // パフォーマンス・速度
  'zap', 'rocket', 'flame', 'wind', 'gauge', 'activity',
  // セキュリティ・信頼
  'shield', 'shield-check', 'lock', 'key', 'badge-check',
  // ツール・機能
  'wrench', 'cog', 'settings', 'tool', 'package', 'box', 'layers',
  // コミュニケーション
  'message-circle', 'message-square', 'mail', 'phone', 'send', 'bell',
  // 人・チーム
  'user', 'users', 'user-check', 'user-plus', 'handshake',
  // データ・分析
  'bar-chart', 'pie-chart', 'line-chart', 'trending-up', 'database', 'brain',
  // ファイル・コンテンツ
  'file-text', 'image', 'video', 'music', 'book', 'book-open', 'newspaper',
  // ナビゲーション・場所
  'map-pin', 'globe', 'compass', 'navigation', 'route',
  // ビジネス
  'briefcase', 'building', 'store', 'shopping-cart', 'credit-card', 'wallet',
  'dollar-sign', 'yen-sign',
  // 時間
  'clock', 'calendar', 'calendar-check', 'timer',
  // インターフェース
  'arrow-right', 'arrow-up-right', 'play', 'play-circle', 'plus', 'plus-circle',
  // 自然・環境
  'leaf', 'sun', 'moon', 'cloud',
  // ハードウェア
  'cable', 'battery', 'cpu', 'monitor', 'smartphone', 'laptop', 'tablet',
  // 学び
  'graduation-cap', 'school', 'book-marked', 'lightbulb',
  // その他よく使う
  'infinity', 'target', 'flag', 'puzzle', 'palette', 'paintbrush',
] as const;

/**
 * kebab-case → PascalCase 変換 (lucide-react の icons オブジェクト参照用)
 *  - "zap" → "Zap"
 *  - "alert-circle" → "AlertCircle"
 *  - "check-circle" → "CheckCircle"
 */
function kebabToPascal(name: string): string {
  return name
    .split('-')
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join('');
}

/**
 * 名前から lucide アイコンを取得。
 * 見つからなければ null。
 */
export function getLucideIcon(name: string | null | undefined): LucideIcon | null {
  if (!name) return null;
  const pascal = kebabToPascal(name.trim());
  const map = icons as unknown as Record<string, LucideIcon>;
  return map[pascal] ?? null;
}
